"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import { GridContainer } from "@/components/layout/GridContainer";
import { ExpertCard } from "@/components/layout/ExpertCard";
import { ExpertPreviewModal, type ExpertPreviewData } from "@/components/landing/ExpertPreviewModal";
import { useIsLandingPage } from "@/lib/landing/LandingContext";
import { trackEvent } from "@/lib/analytics";
import { RichTextField } from "@/components/typography/RichTextField";
import { ExpandableText } from "@/components/typography/ExpandableText";
import { Instagram, LinkedIn } from "@/components/icons";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { AssetStoryblok, MultilinkStoryblok } from "@/component-types-sb";

type ExpertStoryblok = {
  name: string;
  title?: string;
  text?: string;
  image?: AssetStoryblok;
  instagram_link?: MultilinkStoryblok;
  linkedin_link?: MultilinkStoryblok;
  _uid: string;
  component: "expert";
  [k: string]: any;
};

type ExpertGalleryStoryblok = {
  headline?: string;
  experts?: (
    | ExpertStoryblok
    | { content: ExpertStoryblok; [k: string]: any }
  )[];
  _uid: string;
  component: "expert_gallery";
  [k: string]: any;
};

type ExpertGalleryProps = {
  blok: ExpertGalleryStoryblok;
};

type ResolvedExpert = ExpertStoryblok & { href?: string };

function resolveExpert(
  item:
    | ExpertStoryblok
    | { content: ExpertStoryblok; full_slug?: string; [k: string]: any },
): ResolvedExpert | null {
  if ("content" in item && item.content?.component === "expert") {
    return {
      ...item.content,
      href: item.full_slug ? `/${item.full_slug}` : undefined,
    };
  }
  if (item.component === "expert") {
    return item as ExpertStoryblok;
  }
  return null;
}

/** 1–2 experts: SpeakerDetail-like horizontal layout */
const ExpertDetailCard = ({ expert, onPreviewClick }: { expert: ResolvedExpert; onPreviewClick?: () => void }) => {
  const instagramHref = getUrlFromLinkObject(expert.instagram_link);
  const linkedinHref = getUrlFromLinkObject(expert.linkedin_link);
  const hasLinks = !onPreviewClick && (instagramHref || linkedinHref);

  return (
    <div
      className={`bg-sand-10 rounded-3xl p-6 flex flex-col gap-6 w-full${
        (expert.href || onPreviewClick) ? " cursor-pointer" : ""
      }`}
      onClick={onPreviewClick || (expert.href ? () => window.open(expert.href!, "_blank", "noopener,noreferrer") : undefined)}
    >
      {/* Mobile: stacked */}
      <div className="flex flex-col gap-6 md:hidden">
        {expert.image?.filename && (
          <div className="relative w-full aspect-[345/155] rounded-2xl overflow-hidden">
            <Image
              src={expert.image.filename}
              alt=""
              fill
              sizes="100vw"
              className="object-cover blur-xl scale-110"
              aria-hidden="true"
            />
            <Image
              src={expert.image.filename}
              alt={expert.image.alt || expert.name || "Expert"}
              fill
              sizes="100vw"
              className="object-contain"
            />
          </div>
        )}
        <div className="flex flex-col gap-4 items-center text-center">
          <div className="flex flex-col text-black">
            {expert.name && <h3 className="text-headline-xl">{expert.name}</h3>}
            {expert.title && <p className="text-copy">{expert.title}</p>}
          </div>
          {expert.text && (
            <ExpandableText lines={4} className="text-center">
              <RichTextField value={expert.text} className="text-copy text-black" />
            </ExpandableText>
          )}
          {hasLinks && (
            <div className="flex flex-col gap-2 items-center">
              <span className="text-copy-sm text-gray-60">Kontakt</span>
              <div className="flex gap-2">
                {instagramHref && (
                  <Link
                    href={instagramHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Instagram className="size-6 text-black" />
                  </Link>
                )}
                {linkedinHref && (
                  <Link
                    href={linkedinHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LinkedIn className="size-6 text-black" />
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tablet+: two-column */}
      <div className="hidden md:flex md:flex-row gap-12 items-start">
        {expert.image?.filename && (
          <div className="relative w-[200px] aspect-square rounded-2xl overflow-hidden shrink-0">
            <Image
              src={expert.image.filename}
              alt={expert.image.alt || expert.name || "Expert"}
              fill
              sizes="200px"
              className="object-cover"
            />
          </div>
        )}
        <div className="flex flex-col gap-4 items-start text-left flex-1">
          <div className="flex flex-col text-black">
            {expert.name && <h3 className="text-headline-xl">{expert.name}</h3>}
            {expert.title && <p className="text-copy">{expert.title}</p>}
          </div>
          {expert.text && (
            <ExpandableText lines={4}>
              <RichTextField value={expert.text} className="text-copy text-black" />
            </ExpandableText>
          )}
          {hasLinks && (
            <div className="flex flex-col gap-2 items-start">
              <span className="text-copy-sm text-gray-60">Kontakt</span>
              <div className="flex gap-2">
                {instagramHref && (
                  <Link
                    href={instagramHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Instagram className="size-6 text-black" />
                  </Link>
                )}
                {linkedinHref && (
                  <Link
                    href={linkedinHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LinkedIn className="size-6 text-black" />
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExpertGallery = ({ blok }: ExpertGalleryProps) => {
  const isLanding = useIsLandingPage();
  const [previewExpert, setPreviewExpert] = useState<ExpertPreviewData | null>(null);

  const experts = (blok.experts || [])
    .map(resolveExpert)
    .filter((e): e is ResolvedExpert => e !== null);

  const isGallery = experts.length >= 3;

  const openExpertPreview = (expert: ResolvedExpert, source: string) => {
    setPreviewExpert({
      name: expert.name,
      title: expert.title,
      text: expert.text,
      image: expert.image,
    });
    trackEvent("content_preview_opened", {
      type: "expert",
      title: expert.name,
      source,
    });
  };

  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const checkCanScroll = useCallback(() => {
    if (!api) return;
    setCanScroll(api.canScrollNext() || api.canScrollPrev());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    checkCanScroll();
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);
    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full">
      <section className="bg-white py-8 md:py-16 flex flex-col gap-4 w-full items-center rounded-3xl md:rounded-none">
        {/* Headline */}
        {blok.headline && (
          <h2 className="text-headline-xl text-black text-center mb-2">
            {blok.headline}
          </h2>
        )}

        {/* 1–2 experts: detail cards */}
        {!isGallery && (
          <GridContainer>
            <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 flex flex-col gap-6">
              {experts.map((expert) => (
                <ExpertDetailCard key={expert._uid} expert={expert} onPreviewClick={isLanding ? () => openExpertPreview(expert, "expert_gallery_detail") : undefined} />
              ))}
            </div>
          </GridContainer>
        )}

        {/* 3+ experts: carousel */}
        {isGallery && (
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: false,
              dragFree: false,
              watchResize: true,
              breakpoints: {
                "(min-width: 768px)": {
                  align: "start",
                  dragFree: true,
                },
              },
            }}
            plugins={[autoplayRef.current]}
            className="w-full relative"
          >
            <GalleryNavPrevious show={canScroll} />
            <CarouselContent
              className={
                canScroll
                  ? "ml-0 pl-4 md:pl-8 items-stretch"
                  : "justify-center items-stretch"
              }
            >
              {experts.map((expert) => (
                <CarouselItem
                  key={expert._uid}
                  className={
                    canScroll
                      ? "pl-4 basis-auto h-auto"
                      : "basis-auto px-2 h-auto"
                  }
                >
                  <ExpertCard
                    name={expert.name}
                    title={expert.title}
                    text={expert.text}
                    image={expert.image}
                    href={expert.href}
                    instagramUrl={getUrlFromLinkObject(expert.instagram_link)}
                    linkedinUrl={getUrlFromLinkObject(expert.linkedin_link)}
                    className="w-[278px] h-full"
                    onPreviewClick={isLanding ? () => openExpertPreview(expert, "expert_gallery") : undefined}
                  />
                </CarouselItem>
              ))}
              {canScroll && <div className="shrink-0 w-4 md:w-8" />}
            </CarouselContent>
            <GalleryNavNext show={canScroll} />
          </Carousel>
        )}
      </section>
      {previewExpert && (
        <ExpertPreviewModal expert={previewExpert} onClose={() => setPreviewExpert(null)} />
      )}
    </div>
  );
};

export default ExpertGallery;
